/**
 * Centralized Ably initialization utility
 * Uses Ably Native Pub/Sub with @ably/laravel-echo
 *
 * Ably handles reconnection internally:
 * - Automatic reconnection every 15s (up to 2 minutes), then 30s
 * - Message continuity: missed messages delivered on reconnect (2-minute window)
 * - Message queuing during disconnection
 * - Resume/Recover modes for seamless session restoration
 */

import * as Ably from 'ably';
import Echo from '@ably/laravel-echo';
import { wsLogger } from '@/shared/lib/logger';
import { resolveApiUrl } from '@/shared/lib/api-host';
import type { TypedEchoInstance } from './types/echo.types';

// Extend Window interface for Ably
declare global {
  interface Window {
    Ably: typeof Ably;
  }
}

let ablyInitialized = false;

/**
 * Initialize Ably globally for Laravel Echo
 * This should be called once during app initialization
 */
export function initializeAbly(): void {
  if (typeof window === 'undefined') {
    return; // Server-side, skip initialization
  }

  if (ablyInitialized) {
    return; // Already initialized
  }

  window.Ably = Ably;
  ablyInitialized = true;
}

/**
 * Create a new Echo instance with Ably Native configuration
 *
 * Uses token-based authentication via Laravel backend:
 * 1. Echo requests token from /api/v1/broadcasting/auth
 * 2. Backend validates user and returns signed Ably token
 * 3. Client uses token to connect to Ably channels
 */
export function createEchoInstance(token: string): TypedEchoInstance {
  // Ensure Ably is initialized
  initializeAbly();

  // Runtime-resolved so the Russia mirror (*.getfacade.ru) authenticates against
  // its own API host rather than the Cloudflare-blocked .ai one. See resolveApiUrl.
  const apiUrl = resolveApiUrl();

  // @ably/laravel-echo configuration
  // Uses native Ably SDK instead of Pusher protocol adapter
  // All connection settings (heartbeats, timeouts) use Ably SDK defaults
  const config = {
    broadcaster: 'ably' as const,
    // Auth endpoint for private channels
    authEndpoint: `${apiUrl}/api/v1/broadcasting/auth`,
    // Bearer token for API authentication
    auth: {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
      },
    },
  };

  wsLogger.debug('Creating Ably Echo instance', {
    authEndpoint: config.authEndpoint,
  });

  return new Echo(config) as unknown as TypedEchoInstance;
}

/**
 * Check if Ably is initialized
 */
export function isAblyInitialized(): boolean {
  return ablyInitialized && typeof window !== 'undefined' && !!window.Ably;
}
